/**
 * @override
 * King Living Blog component with lazy loading Rich Text links support
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Skeleton, SkeletonText, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import BlogHeading from '@amblique/king-living/app/components/blog-heading';
import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {useBlogLinks} from '@amblique/king-living/app/contentful/hooks/use-blog-links';
import {hasEmbeddedContent} from '@amblique/king-living/app/contentful/utils/rich-text';
import {formatBlogPublishedDate} from '@amblique/king-living/app/contentful/utils/format-blog-published-date';
export const Blog = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/blog', {component, isComplete, isFetching});
  const {className: incomingClassName, ...restProps} = props;
  const {className: styleRootClass, ...styleRootRest} = styles.root || {};

  const rootClassName = ['component--blog-header-article', styleRootClass, incomingClassName]
    .filter(Boolean)
    .join(' ');

  const {
    title,
    author,
    datePublished,
    duration,
    description,
    blogContent,
    blogHeaderMedia,
    shareThisPostCollection,
  } = component;

  const hasContent = blogContent?.json;

  // Check if we need to fetch links
  const needsLinks = useMemo(() => {
    if (!hasContent || blogContent?.links) return false;

    return hasEmbeddedContent(blogContent.json);
  }, [hasContent, blogContent]);

  // Lazy load links only when needed (with error handling)
  const {links, isLoading: isLoadingLinks, hasError: linksError} = useBlogLinks(component.sys?.id, {
    enabled: needsLinks,
  });

  // Merge blogContent with fetched links (skip if links failed - content will render without embedded entries)
  const blogContentWithLinks = useMemo(() => {
    if (!hasContent) return blogContent;
    if (blogContent?.links) return blogContent;
    if (!links) return blogContent; // No links or links failed - render without embedded entries

    return {
      ...blogContent,
      links,
    };
  }, [blogContent, links, hasContent]);

  const shareLinks = (shareThisPostCollection?.items || []).filter(Boolean);

  // Show loading state only if fetching links AND not errored (if errored, show content without embedded)
  const isLoading = (!isComplete && isFetching) || (needsLinks && isLoadingLinks && !linksError);

  if (isLoading) {
    return (
      <Box {...styleRootRest} {...restProps} className={rootClassName}>
        <Skeleton height="400px" mb={8} width="100%" />
        <Skeleton height="48px" mb={4} width="80%" />
        <SkeletonText mb={4} noOfLines={1} width="40%" />
        <SkeletonText noOfLines={10} spacing={4} />
      </Box>
    );
  }

  return (
    <Box {...styleRootRest} {...restProps} className={rootClassName}>
      <BlogHeading
        author={author}
        datePublished={formatBlogPublishedDate(datePublished)}
        description={description}
        readTime={duration}
        shareLinks={shareLinks}
        title={title}
      />

      {blogHeaderMedia?.url && (
        <Box {...styles.headerMedia}>
          {/* Blog hero = LCP element: load eagerly with high priority, never lazy. */}
          <img
            alt={blogHeaderMedia.title || title}
            fetchpriority="high"
            loading="eager"
            src={blogHeaderMedia.url}
          />
        </Box>
      )}

      {hasContent && (
        <Box {...styles.content}>
          <RichTextContent content={blogContentWithLinks} />
        </Box>
      )}
    </Box>
  );
};

Blog.displayName = 'Blog';
Blog.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  className: PropTypes.string,
};
